import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const fmt = v => v !== undefined && v !== null ? 'R$ ' + parseFloat(v).toLocaleString('pt-BR', { minimumFractionDigits: 2 }) : 'R$ 0,00';
const fmtData = d => d ? new Date(d).toLocaleDateString('pt-BR') : '—';

const card = { background: '#fff', border: '0.5px solid rgba(26,58,92,0.12)', borderRadius: '10px', padding: '16px 18px' };
const cardTitle = { fontSize: '11px', fontWeight: '600', color: '#6B7A8D', textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: '12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' };
const kpiBox = { background: '#F4F6F9', borderRadius: '8px', padding: '12px 14px' };
const th = { padding: '0 10px 8px 0', color: '#9AA3AE', fontSize: '10.5px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.4px', textAlign: 'left', borderBottom: '0.5px solid rgba(26,58,92,0.1)' };
const td = { padding: '9px 10px 9px 0', borderBottom: '0.5px solid rgba(26,58,92,0.07)', fontSize: '13px', color: '#1C2533', verticalAlign: 'middle' };
const badge = (c) => ({ fontSize: '11px', padding: '2px 8px', borderRadius: '20px', fontWeight: '500', background: c === 'info' ? '#E6F1FB' : c === 'warn' ? '#FFF3E0' : c === 'danger' ? '#FDECEA' : '#EAF5EE', color: c === 'info' ? '#185FA5' : c === 'warn' ? '#B06000' : c === 'danger' ? '#C0392B' : '#2D7D4F' });
const kpiVal = (c, size) => ({ fontSize: size || '24px', fontWeight: '700', letterSpacing: '-0.5px', fontFamily: "'DM Mono',monospace,sans-serif", color: c === 'green' ? '#2D7D4F' : c === 'blue' ? '#1E5FA5' : c === 'red' ? '#C0392B' : c === 'orange' ? '#B06000' : '#1C2533' });
const diasBadge = (d) => { const n = parseInt(d || 0); return { display: 'inline-block', padding: '2px 8px', borderRadius: '20px', fontSize: '11px', fontWeight: '600', background: n < 20 ? '#EAF5EE' : n < 35 ? '#FFF3E0' : '#FDECEA', color: n < 20 ? '#2D7D4F' : n < 35 ? '#B06000' : '#C0392B' }; };
const variacaoBadge = (atual, anterior) => {
  if (!anterior || parseFloat(anterior) === 0) return null;
  const pct = ((parseFloat(atual) - parseFloat(anterior)) / parseFloat(anterior) * 100).toFixed(0);
  const up = pct >= 0;
  return <span style={{ fontSize: '11px', fontWeight: '600', color: up ? '#2D7D4F' : '#C0392B', background: up ? '#EAF5EE' : '#FDECEA', padding: '2px 6px', borderRadius: '6px' }}>{up ? '▲' : '▼'} {Math.abs(pct)}%</span>;
};

export default function Dashboard() {
  const [dados, setDados] = useState(null);
  const [editandoMeta, setEditandoMeta] = useState(false);
  const [novaMeta, setNovaMeta] = useState('');
  const [salvandoMeta, setSalvandoMeta] = useState(false);
  const navigate = useNavigate();
  const { usuario } = useAuth();
  const isAdmin = usuario?.nivel === 'admin' || usuario?.nivel === 'gerente';

  const carregar = () => {
    api.get('/relatorios/dashboard').then(r => setDados(r.data)).catch(console.error);
  };

  useEffect(() => { carregar(); }, []);

  const salvarMeta = async () => {
    if (!novaMeta || isNaN(novaMeta) || parseInt(novaMeta) <= 0) { alert('Meta inválida'); return; }
    setSalvandoMeta(true);
    try {
      await api.put('/relatorios/config/meta_vendas_mes', { valor: novaMeta });
      setEditandoMeta(false);
      carregar();
    } catch (e) { alert('Erro ao salvar meta'); }
    finally { setSalvandoMeta(false); }
  };

  if (!dados) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '200px', color: '#9AA3AE', fontSize: '14px' }}>Carregando...</div>
  );

  const vendas_mes = parseInt(dados.vendas_mes.total || 0);
  const meta = parseInt(dados.meta_vendas || 20);
  const pctMeta = Math.min(Math.round((vendas_mes / meta) * 100), 100);
  const corMeta = pctMeta >= 100 ? '#2D7D4F' : pctMeta >= 60 ? '#B06000' : '#C0392B';
  const mesAtual = new Date().toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });

  // ===================== DASHBOARD VENDEDOR =====================
  if (!isAdmin) {
    return (
      <div>
        <div style={{ marginBottom: '20px' }}>
          <div style={{ fontSize: '18px', fontWeight: '700', color: '#1C2533' }}>Olá, {usuario?.nome?.split(' ')[0]} 👋</div>
          <div style={{ fontSize: '13px', color: '#6B7A8D', marginTop: '2px' }}>Autop Motos · {mesAtual}</div>
        </div>

        {/* Meta do mês — vendedor */}
        <div style={{ ...card, borderLeft: `3px solid ${corMeta}`, marginBottom: '14px' }}>
          <div style={cardTitle}><span>Sua meta do mês</span></div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '8px' }}>
            <div>
              <span style={{ fontSize: '36px', fontWeight: '700', fontFamily: "'DM Mono',monospace", color: corMeta }}>{vendas_mes}</span>
              <span style={{ fontSize: '16px', color: '#9AA3AE', marginLeft: '6px' }}>/ {meta} vendas</span>
            </div>
            <span style={{ fontSize: '28px', fontWeight: '700', fontFamily: "'DM Mono',monospace", color: corMeta }}>{pctMeta}%</span>
          </div>
          <div style={{ height: '10px', background: '#F0F3F7', borderRadius: '5px', overflow: 'hidden', marginBottom: '8px' }}>
            <div style={{ height: '100%', width: pctMeta + '%', background: corMeta, borderRadius: '5px', transition: 'width 0.5s ease' }}></div>
          </div>
          <div style={{ fontSize: '13px', color: '#6B7A8D' }}>
            {meta - vendas_mes > 0 ? `Faltam ${meta - vendas_mes} vendas para atingir a meta` : '🎉 Meta atingida! Parabéns!'}
          </div>
        </div>

        {/* Ranking vendedores */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
          <div style={card}>
            <div style={cardTitle}>
              <span>Ranking vendedores</span>
              <span style={badge('info')}>mês</span>
            </div>
            {dados.ranking.length === 0 ? (
              <div style={{ color: '#9AA3AE', fontSize: '13px', textAlign: 'center', padding: '16px 0' }}>Sem vendas no mês</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {dados.ranking.map((v, i) => {
                  const maxV = dados.ranking[0]?.total || 1;
                  const pct = Math.round((v.total / maxV) * 100);
                  const cores = ['#B8860B', '#6B7A8D', '#BF360C', '#9AA3AE', '#9AA3AE'];
                  const bgs = ['#FFF8E1', '#F5F5F5', '#FBE9E7', '#F4F6F9', '#F4F6F9'];
                  const isMe = v.nome === usuario?.nome;
                  return (
                    <div key={v.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: isMe ? '8px 10px' : '0', background: isMe ? '#E6F1FB' : 'transparent', borderRadius: isMe ? '8px' : '0' }}>
                      <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: bgs[i], display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: '700', color: cores[i], flexShrink: 0 }}>{i + 1}</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '13px', fontWeight: isMe ? '700' : '500', color: isMe ? '#185FA5' : '#1C2533' }}>{v.nome} {isMe && '(você)'}</div>
                        <div style={{ height: '4px', borderRadius: '3px', background: '#F0F3F7', marginTop: '4px' }}>
                          <div style={{ height: '100%', borderRadius: '3px', background: isMe ? '#185FA5' : cores[i], width: pct + '%' }}></div>
                        </div>
                      </div>
                      <div style={{ fontSize: '16px', fontWeight: '700', fontFamily: "'DM Mono',monospace", color: '#1C2533' }}>{v.total}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Estoque disponível */}
          <div style={card}>
            <div style={cardTitle}>
              <span>Estoque disponível</span>
              <button onClick={() => navigate('/estoque')} style={{ fontSize: '11px', color: '#1E5FA5', background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'DM Sans',system-ui,sans-serif", fontWeight: '500' }}>Ver tudo →</button>
            </div>
            <div style={kpiBox}>
              <div style={kpiVal('orange')}>{dados.estoque.total}</div>
              <div style={{ fontSize: '12px', color: '#9AA3AE', marginTop: '4px' }}>motos para vender</div>
            </div>
            <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {dados.mais_parados.slice(0, 4).map(v => (
                <div key={v.id} onClick={() => navigate('/estoque/' + v.id)}
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 10px', background: '#F4F6F9', borderRadius: '7px', cursor: 'pointer' }}>
                  <div style={{ fontSize: '12px', fontWeight: '500', color: '#1C2533' }}>{v.marca} {v.modelo} {v.ano_mod}</div>
                  <span style={diasBadge(v.dias)}>{parseInt(v.dias)}d</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ===================== DASHBOARD ADMIN =====================
  return (
    <div>
      <div style={{ marginBottom: '20px' }}>
        <div style={{ fontSize: '18px', fontWeight: '700', color: '#1C2533', letterSpacing: '-0.3px' }}>Bom dia 👋</div>
        <div style={{ fontSize: '13px', color: '#6B7A8D', marginTop: '2px' }}>Autop Motos · Montese, Fortaleza · {mesAtual}</div>
      </div>

      {/* KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '12px', marginBottom: '12px' }}>
        <div style={card}>
          <div style={cardTitle}><span>Motos em estoque</span>{dados.estoque.parados > 0 && <span style={badge('warn')}>{dados.estoque.parados} +30d</span>}</div>
          <div style={kpiBox}><div style={kpiVal('orange')}>{dados.estoque.total}</div><div style={{ fontSize: '11px', color: '#9AA3AE', marginTop: '4px' }}>unidades · {fmt(dados.estoque.valor_total)}</div></div>
        </div>
        <div style={card}>
          <div style={cardTitle}><span>Vendas no mês</span>{variacaoBadge(dados.vendas_mes.total, dados.vendas_mes_ant.total)}</div>
          <div style={kpiBox}><div style={kpiVal('blue')}>{dados.vendas_mes.total}</div><div style={{ fontSize: '11px', color: '#9AA3AE', marginTop: '4px' }}>mês anterior: {dados.vendas_mes_ant.total}</div></div>
        </div>
        <div style={card}>
          <div style={cardTitle}><span>Receita do mês</span>{variacaoBadge(dados.vendas_mes.receita, dados.vendas_mes_ant.receita)}</div>
          <div style={kpiBox}><div style={{ ...kpiVal('green'), fontSize: '18px' }}>{fmt(dados.vendas_mes.receita)}</div><div style={{ fontSize: '11px', color: '#9AA3AE', marginTop: '4px' }}>lucro: {fmt(dados.lucro_mes)}</div></div>
        </div>
        <div style={card}>
          <div style={cardTitle}><span>Ticket médio</span></div>
          <div style={kpiBox}><div style={{ ...kpiVal('blue'), fontSize: '18px' }}>{fmt(dados.ticket_medio)}</div><div style={{ fontSize: '11px', color: '#9AA3AE', marginTop: '4px' }}>por venda no mês</div></div>
        </div>
      </div>

      {/* Meta + Financeiro + Capital + Ranking */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr 1fr', gap: '12px', marginBottom: '12px' }}>
        <div style={{ ...card, borderLeft: `3px solid ${corMeta}` }}>
          <div style={cardTitle}>
            <span>Meta do mês</span>
            {!editandoMeta && (
              <button onClick={() => { setNovaMeta(String(meta)); setEditandoMeta(true); }}
                style={{ fontSize: '11px', color: '#1E5FA5', background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'DM Sans',system-ui,sans-serif", fontWeight: '600' }}>Editar</button>
            )}
          </div>
          {editandoMeta ? (
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '12px' }}>
              <input type='number' value={novaMeta} onChange={e => setNovaMeta(e.target.value)}
                style={{ flex: 1, padding: '6px 10px', background: '#F4F6F9', border: '0.5px solid rgba(26,58,92,0.2)', borderRadius: '6px', fontSize: '14px', fontFamily: "'DM Sans',system-ui,sans-serif", color: '#1C2533' }} />
              <button onClick={salvarMeta} disabled={salvandoMeta}
                style={{ padding: '6px 12px', background: '#1A3A5C', border: 'none', color: '#fff', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontFamily: "'DM Sans',system-ui,sans-serif", fontWeight: '600' }}>
                {salvandoMeta ? '...' : 'OK'}
              </button>
              <button onClick={() => setEditandoMeta(false)}
                style={{ padding: '6px 10px', background: 'transparent', border: '0.5px solid rgba(26,58,92,0.2)', color: '#6B7A8D', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontFamily: "'DM Sans',system-ui,sans-serif" }}>×</button>
            </div>
          ) : (
            <div style={{ marginBottom: '10px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '6px' }}>
                <div><span style={{ fontSize: '28px', fontWeight: '700', fontFamily: "'DM Mono',monospace", color: corMeta }}>{vendas_mes}</span><span style={{ fontSize: '14px', color: '#9AA3AE', marginLeft: '4px' }}>/ {meta} vendas</span></div>
                <span style={{ fontSize: '20px', fontWeight: '700', fontFamily: "'DM Mono',monospace", color: corMeta }}>{pctMeta}%</span>
              </div>
            </div>
          )}
          <div style={{ height: '8px', background: '#F0F3F7', borderRadius: '4px', overflow: 'hidden', marginBottom: '8px' }}>
            <div style={{ height: '100%', width: pctMeta + '%', background: corMeta, borderRadius: '4px', transition: 'width 0.5s ease' }}></div>
          </div>
          <div style={{ fontSize: '11px', color: '#9AA3AE' }}>{meta - vendas_mes > 0 ? `Faltam ${meta - vendas_mes} vendas` : '🎉 Meta atingida!'}</div>
        </div>

        <div style={card}>
          <div style={cardTitle}><span>Financeiro</span></div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div style={{ ...kpiBox, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}><span style={{ fontSize: '12px', color: '#6B7A8D' }}>A receber</span><span style={{ fontFamily: "'DM Mono',monospace", fontSize: '13px', fontWeight: '700', color: '#2D7D4F' }}>{fmt(dados.a_receber.total)}</span></div>
            <div style={{ ...kpiBox, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}><span style={{ fontSize: '12px', color: '#6B7A8D' }}>A pagar</span><span style={{ fontFamily: "'DM Mono',monospace", fontSize: '13px', fontWeight: '700', color: '#C0392B' }}>{fmt(dados.a_pagar.total)}</span></div>
            <div style={{ ...kpiBox, display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#1A3A5C' }}><span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>Saldo</span><span style={{ fontFamily: "'DM Mono',monospace", fontSize: '13px', fontWeight: '700', color: '#fff' }}>{fmt(parseFloat(dados.a_receber.total) - parseFloat(dados.a_pagar.total))}</span></div>
          </div>
        </div>

        <div style={card}>
          <div style={cardTitle}><span>Capital imobilizado</span></div>
          <div style={kpiBox}><div style={{ ...kpiVal('blue'), fontSize: '16px' }}>{fmt(dados.estoque.valor_total)}</div><div style={{ fontSize: '11px', color: '#9AA3AE', marginTop: '4px' }}>em {dados.estoque.total} veículos</div></div>
          <div style={{ marginTop: '10px', height: '0.5px', background: 'rgba(26,58,92,0.08)' }}></div>
          <div style={{ marginTop: '10px' }}><div style={{ fontSize: '11px', color: '#9AA3AE', marginBottom: '4px' }}>Custo médio/unidade</div><div style={{ fontFamily: "'DM Mono',monospace", fontSize: '13px', fontWeight: '700', color: '#1C2533' }}>{dados.estoque.total > 0 ? fmt(parseFloat(dados.estoque.custo_total) / parseInt(dados.estoque.total)) : 'R$ 0,00'}</div></div>
        </div>

        <div style={card}>
          <div style={cardTitle}><span>Ranking vendedores</span><span style={badge('info')}>mês</span></div>
          {dados.ranking.length === 0 ? <div style={{ color: '#9AA3AE', fontSize: '13px', textAlign: 'center', padding: '16px 0' }}>Sem vendas</div> : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {dados.ranking.map((v, i) => {
                const maxV = dados.ranking[0]?.total || 1;
                const pct = Math.round((v.total / maxV) * 100);
                const cores = ['#B8860B', '#6B7A8D', '#BF360C', '#9AA3AE', '#9AA3AE'];
                const bgs = ['#FFF8E1', '#F5F5F5', '#FBE9E7', '#F4F6F9', '#F4F6F9'];
                return (
                  <div key={v.id} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '20px', height: '20px', borderRadius: '50%', background: bgs[i], display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: '700', color: cores[i], flexShrink: 0 }}>{i + 1}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '12px', fontWeight: '500', color: '#1C2533' }}>{v.nome}</div>
                      <div style={{ height: '4px', borderRadius: '3px', background: '#F0F3F7', marginTop: '4px' }}><div style={{ height: '100%', borderRadius: '3px', background: cores[i], width: pct + '%' }}></div></div>
                    </div>
                    <div style={{ fontSize: '14px', fontWeight: '700', fontFamily: "'DM Mono',monospace", color: '#1C2533' }}>{v.total}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Tabelas */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
        <div style={card}>
          <div style={cardTitle}><span>Estoque — mais tempo parado</span><button onClick={() => navigate('/estoque')} style={{ fontSize: '11px', color: '#1E5FA5', background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'DM Sans',system-ui,sans-serif", fontWeight: '500' }}>Ver tudo →</button></div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>{['Veículo', 'Venda', 'Dias'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
            <tbody>
              {dados.mais_parados.length === 0 && <tr><td colSpan={3} style={{ ...td, textAlign: 'center', color: '#9AA3AE', padding: '20px 0' }}>Sem estoque</td></tr>}
              {dados.mais_parados.map(v => (
                <tr key={v.id} style={{ cursor: 'pointer' }} onClick={() => navigate('/estoque/' + v.id)}>
                  <td style={td}><div style={{ fontWeight: '500', fontSize: '13px' }}>{v.marca} {v.modelo}</div><div style={{ fontSize: '11px', color: '#9AA3AE' }}>{v.ano_mod}</div></td>
                  <td style={{ ...td, fontFamily: "'DM Mono',monospace", fontSize: '12px', color: '#2D7D4F', fontWeight: '600' }}>{fmt(v.valor_venda)}</td>
                  <td style={td}><span style={diasBadge(v.dias)}>{parseInt(v.dias)}d</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={card}>
          <div style={cardTitle}><span>Últimas vendas</span><button onClick={() => navigate('/vendas')} style={{ fontSize: '11px', color: '#1E5FA5', background: 'none', border: 'none', cursor: 'pointer', fontFamily: "'DM Sans',system-ui,sans-serif", fontWeight: '500' }}>Ver tudo →</button></div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>{['Data', 'Veículo', 'Cliente', 'Valor'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
            <tbody>
              {dados.ultimas_vendas.length === 0 && <tr><td colSpan={4} style={{ ...td, textAlign: 'center', color: '#9AA3AE', padding: '20px 0' }}>Sem vendas</td></tr>}
              {dados.ultimas_vendas.map((v, i) => (
                <tr key={i}>
                  <td style={{ ...td, color: '#6B7A8D', fontSize: '11px', fontFamily: "'DM Mono',monospace" }}>{fmtData(v.data_venda)}</td>
                  <td style={{ ...td, fontWeight: '500', fontSize: '12px' }}>{v.marca} {v.modelo}</td>
                  <td style={{ ...td, color: '#6B7A8D', fontSize: '12px' }}>{v.cliente_nome}</td>
                  <td style={{ ...td, fontFamily: "'DM Mono',monospace", fontSize: '12px', color: '#2D7D4F', fontWeight: '600' }}>{fmt(v.valor_final)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
